import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  useAdminChallengeDetail,
  type ChallengeDetail,
  type AiMetadata,
} from "../../hooks/useAdminChallengeDetail";
import {
  SkillKeysByDiscipline,
  type DisciplineSlug,
  type ContentReviewStatus,
} from "@jumpbean/shared";

/**
 * ChallengeDetailPage - Admin page to view and edit individual challenge details.
 *
 * Features:
 * - Display all challenge fields including content JSON and skill_tags
 * - Show AI metadata if challenge was AI-generated
 * - Edit form for challenge content with JSON editor
 * - Skill tags editor with multi-select
 * - Content preview
 * - Review actions (Approve, Reject, Request Revision)
 * - Review notes field
 * - Regenerate with AI action
 * - Auto-tag skills button
 */
export function ChallengeDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    challenge,
    isLoading,
    error,
    isSaving,
    saveError,
    updateChallenge,
    updateReviewStatus,
    regenerateWithAI,
    autoTagSkills,
    refetch,
  } = useAdminChallengeDetail(id);

  const [activeTab, setActiveTab] = useState<"details" | "edit" | "preview">(
    "details"
  );
  const [editedContent, setEditedContent] = useState<string>("");
  const [editedSkillTags, setEditedSkillTags] = useState<string[]>([]);
  const [reviewNotes, setReviewNotes] = useState("");
  const [jsonError, setJsonError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Initialize edit state when challenge loads
  useEffect(() => {
    if (challenge) {
      setEditedContent(JSON.stringify(challenge.content, null, 2));
      setEditedSkillTags(challenge.skillTags);
      setReviewNotes(challenge.reviewNotes || "");
    }
  }, [challenge]);

  // Clear success message after 3 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (error || !challenge) {
    return (
      <div className="space-y-6">
        <BackLink />
        <div className="bg-red-50 border border-red-200 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-red-800 mb-2">
            Error Loading Challenge
          </h2>
          <p className="text-sm text-red-600">{error || "Challenge not found"}</p>
        </div>
      </div>
    );
  }

  const handleContentChange = (value: string) => {
    setEditedContent(value);
    try {
      JSON.parse(value);
      setJsonError(null);
    } catch {
      setJsonError("Invalid JSON");
    }
  };

  const handleSkillToggle = (skill: string) => {
    setEditedSkillTags((prev) =>
      prev.includes(skill) ? prev.filter((s) => s !== skill) : [...prev, skill]
    );
  };

  const handleSave = async () => {
    if (jsonError) {
      return;
    }

    try {
      const parsedContent = JSON.parse(editedContent);
      const result = await updateChallenge({
        content: parsedContent,
        skillTags: editedSkillTags,
      });

      if (result.success) {
        setSuccessMessage("Challenge saved successfully");
      }
    } catch {
      // JSON parse error already handled
    }
  };

  const handleReviewAction = async (
    status: ContentReviewStatus,
    notes?: string
  ) => {
    const result = await updateReviewStatus(status, notes || reviewNotes);
    if (result.success) {
      setSuccessMessage(`Challenge ${status}`);
    }
  };

  const handleRegenerate = async () => {
    const result = await regenerateWithAI();
    if (result.success && result.content) {
      setEditedContent(JSON.stringify(result.content, null, 2));
      if (result.skillTags) {
        setEditedSkillTags(result.skillTags);
      }
      setSuccessMessage("Content regenerated - review and save changes");
      setActiveTab("edit");
    }
  };

  const handleAutoTag = async () => {
    const result = await autoTagSkills();
    if (result.success && result.suggestedSkills.length > 0) {
      // Add suggested skills that aren't already selected
      setEditedSkillTags((prev) => {
        const newSkills = result.suggestedSkills.filter(
          (s) => !prev.includes(s)
        );
        return [...prev, ...newSkills];
      });
      setSuccessMessage(`Added ${result.suggestedSkills.length} skill suggestions`);
      setActiveTab("edit");
    }
  };

  const availableSkills = SkillKeysByDiscipline[challenge.disciplineSlug] || [];

  return (
    <div className="space-y-6">
      {/* Back link */}
      <BackLink />

      {/* Success message */}
      {successMessage && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-sm text-green-700 flex items-center gap-2">
            <CheckIcon className="w-5 h-5" />
            {successMessage}
          </p>
        </div>
      )}

      {/* Error message */}
      {saveError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-600">{saveError}</p>
        </div>
      )}

      {/* Challenge header */}
      <ChallengeHeader challenge={challenge} />

      {/* AI Metadata (if AI-generated) */}
      {challenge.aiMetadata && (
        <AIMetadataCard metadata={challenge.aiMetadata} />
      )}

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex gap-6">
          {(
            [
              { id: "details", label: "Details" },
              { id: "edit", label: "Edit" },
              { id: "preview", label: "Preview" },
            ] as const
          ).map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                py-3 px-1 text-sm font-medium border-b-2 transition-colors
                ${
                  activeTab === tab.id
                    ? "border-magic-500 text-magic-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div className="min-h-[400px]">
        {activeTab === "details" && (
          <DetailsTab
            challenge={challenge}
            reviewNotes={reviewNotes}
            onReviewNotesChange={setReviewNotes}
            onReviewAction={handleReviewAction}
            onRegenerate={handleRegenerate}
            onAutoTag={handleAutoTag}
            isSaving={isSaving}
          />
        )}
        {activeTab === "edit" && (
          <EditTab
            challenge={challenge}
            editedContent={editedContent}
            editedSkillTags={editedSkillTags}
            availableSkills={availableSkills as readonly string[]}
            jsonError={jsonError}
            onContentChange={handleContentChange}
            onSkillToggle={handleSkillToggle}
            onSave={handleSave}
            isSaving={isSaving}
          />
        )}
        {activeTab === "preview" && (
          <PreviewTab challenge={challenge} editedContent={editedContent} />
        )}
      </div>
    </div>
  );
}

// ----- Tab Components -----

function DetailsTab({
  challenge,
  reviewNotes,
  onReviewNotesChange,
  onReviewAction,
  onRegenerate,
  onAutoTag,
  isSaving,
}: {
  challenge: ChallengeDetail;
  reviewNotes: string;
  onReviewNotesChange: (notes: string) => void;
  onReviewAction: (status: ContentReviewStatus, notes?: string) => void;
  onRegenerate: () => void;
  onAutoTag: () => void;
  isSaving: boolean;
}) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Challenge Info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Challenge Information
        </h2>
        <div className="space-y-4">
          <InfoRow label="Challenge #" value={String(challenge.challengeNumber)} />
          <InfoRow label="Type" value={formatChallengeType(challenge.type)} />
          <InfoRow label="Difficulty" value={`Level ${challenge.difficulty}`} />
          <InfoRow
            label="Active"
            value={challenge.isActive ? "Yes" : "No"}
            valueClassName={challenge.isActive ? "text-green-600" : "text-gray-500"}
          />
          <InfoRow
            label="Created"
            value={challenge.aiMetadata?.generated_at
              ? formatDateTime(challenge.aiMetadata.generated_at)
              : "Unknown"}
          />
        </div>
      </div>

      {/* Skill Tags */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Skill Tags
        </h2>
        {challenge.skillTags.length === 0 ? (
          <p className="text-sm text-gray-500">No skills assigned</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {challenge.skillTags.map((skill) => (
              <SkillBadge
                key={skill}
                skill={skill}
                discipline={challenge.disciplineSlug}
              />
            ))}
          </div>
        )}
      </div>

      {/* Review Status & Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Review Status
        </h2>
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex-1 space-y-4">
            <div className="flex items-center gap-3">
              <StatusBadge status={challenge.reviewStatus} />
              {challenge.reviewedAt && (
                <span className="text-sm text-gray-500">
                  Reviewed {formatRelativeTime(challenge.reviewedAt)}
                </span>
              )}
            </div>

            {/* Review notes input */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Review Notes
              </label>
              <textarea
                value={reviewNotes}
                onChange={(e) => onReviewNotesChange(e.target.value)}
                placeholder="Add notes for this review..."
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm
                  focus:ring-2 focus:ring-magic-500 focus:border-transparent"
                rows={3}
              />
            </div>
          </div>

          {/* Review action buttons */}
          <div className="flex flex-col gap-3 lg:w-48">
            <button
              onClick={() => onReviewAction("approved")}
              disabled={isSaving || challenge.reviewStatus === "approved"}
              className="w-full px-4 py-2 text-sm font-medium text-white bg-green-600
                rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed
                transition-colors"
            >
              Approve
            </button>
            <button
              onClick={() => onReviewAction("needs_revision")}
              disabled={isSaving}
              className="w-full px-4 py-2 text-sm font-medium text-orange-700 bg-orange-100
                rounded-lg hover:bg-orange-200 disabled:bg-gray-100 disabled:text-gray-400
                disabled:cursor-not-allowed transition-colors"
            >
              Request Revision
            </button>
            <button
              onClick={() => onReviewAction("rejected")}
              disabled={isSaving || challenge.reviewStatus === "rejected"}
              className="w-full px-4 py-2 text-sm font-medium text-red-700 bg-red-100
                rounded-lg hover:bg-red-200 disabled:bg-gray-100 disabled:text-gray-400
                disabled:cursor-not-allowed transition-colors"
            >
              Reject
            </button>
          </div>
        </div>
      </div>

      {/* AI Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">AI Actions</h2>
        <div className="flex flex-wrap gap-4">
          <button
            onClick={onRegenerate}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium
              text-magic-700 bg-magic-100 rounded-lg hover:bg-magic-200
              disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed
              transition-colors"
          >
            <SparklesIcon className="w-5 h-5" />
            Regenerate with AI
          </button>
          <button
            onClick={onAutoTag}
            disabled={isSaving}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium
              text-purple-700 bg-purple-100 rounded-lg hover:bg-purple-200
              disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed
              transition-colors"
          >
            <TagIcon className="w-5 h-5" />
            Auto-tag Skills
          </button>
        </div>
        <p className="mt-3 text-xs text-gray-500">
          Regenerate will create a new version of the content. Auto-tag will suggest
          appropriate skill tags based on the challenge type and content.
        </p>
      </div>

      {/* Content JSON (read-only view) */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 lg:col-span-2">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Content (JSON)
        </h2>
        <pre className="bg-gray-50 p-4 rounded-lg overflow-x-auto text-xs font-mono text-gray-700">
          {JSON.stringify(challenge.content, null, 2)}
        </pre>
      </div>
    </div>
  );
}

function EditTab({
  challenge,
  editedContent,
  editedSkillTags,
  availableSkills,
  jsonError,
  onContentChange,
  onSkillToggle,
  onSave,
  isSaving,
}: {
  challenge: ChallengeDetail;
  editedContent: string;
  editedSkillTags: string[];
  availableSkills: readonly string[];
  jsonError: string | null;
  onContentChange: (value: string) => void;
  onSkillToggle: (skill: string) => void;
  onSave: () => void;
  isSaving: boolean;
}) {
  const hasChanges =
    editedContent !== JSON.stringify(challenge.content, null, 2) ||
    JSON.stringify(editedSkillTags.sort()) !==
      JSON.stringify([...challenge.skillTags].sort());

  return (
    <div className="space-y-6">
      {/* Skill Tags Editor */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          Edit Skill Tags
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Select the skills assessed by this challenge:
        </p>
        <div className="flex flex-wrap gap-2">
          {availableSkills.map((skill) => (
            <button
              key={skill}
              onClick={() => onSkillToggle(skill)}
              className={`
                px-3 py-1.5 rounded-full text-sm font-medium transition-colors
                ${
                  editedSkillTags.includes(skill)
                    ? "bg-magic-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }
              `}
            >
              {formatSkillName(skill)}
            </button>
          ))}
        </div>
      </div>

      {/* Content JSON Editor */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Edit Content</h2>
          {jsonError && (
            <span className="text-sm text-red-600">{jsonError}</span>
          )}
        </div>
        <textarea
          value={editedContent}
          onChange={(e) => onContentChange(e.target.value)}
          className={`
            w-full h-96 px-4 py-3 font-mono text-sm
            bg-gray-50 border rounded-lg
            focus:ring-2 focus:ring-magic-500 focus:border-transparent
            ${jsonError ? "border-red-300" : "border-gray-200"}
          `}
          spellCheck={false}
        />
      </div>

      {/* Save button */}
      <div className="flex justify-end gap-4">
        <Link
          to="/admin/challenges"
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border
            border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        >
          Cancel
        </Link>
        <button
          onClick={onSave}
          disabled={isSaving || !!jsonError || !hasChanges}
          className="px-6 py-2 text-sm font-medium text-white bg-magic-600 rounded-lg
            hover:bg-magic-700 disabled:bg-gray-300 disabled:cursor-not-allowed
            transition-colors"
        >
          {isSaving ? "Saving..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
}

function PreviewTab({
  challenge,
  editedContent,
}: {
  challenge: ChallengeDetail;
  editedContent: string;
}) {
  let parsedContent: Record<string, unknown>;
  try {
    parsedContent = JSON.parse(editedContent);
  } catch {
    parsedContent = challenge.content;
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <h2 className="text-lg font-semibold text-gray-900 mb-4">
        Challenge Preview
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        This is a simplified preview of how the challenge content will appear.
        The actual game may render it differently.
      </p>

      <div className="border border-gray-200 rounded-xl p-6 bg-gray-50">
        <ChallengePreview
          type={challenge.type}
          discipline={challenge.disciplineSlug}
          content={parsedContent}
        />
      </div>

      {/* Skills assessed */}
      <div className="mt-6 pt-6 border-t border-gray-200">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">
          Skills Assessed
        </h3>
        <div className="flex flex-wrap gap-2">
          {challenge.skillTags.map((skill) => (
            <SkillBadge
              key={skill}
              skill={skill}
              discipline={challenge.disciplineSlug}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

// ----- Helper Components -----

function BackLink() {
  return (
    <Link
      to="/admin/challenges"
      className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
    >
      <ArrowLeftIcon className="w-4 h-4" />
      Back to Challenges
    </Link>
  );
}

function LoadingSkeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="h-4 bg-gray-200 rounded w-32" />
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-lg bg-gray-200" />
          <div className="flex-1">
            <div className="h-6 bg-gray-200 rounded w-48 mb-2" />
            <div className="h-4 bg-gray-100 rounded w-64" />
          </div>
        </div>
      </div>
      <div className="h-10 bg-gray-200 rounded w-full" />
      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4" />
          <div className="space-y-3">
            <div className="h-4 bg-gray-100 rounded w-full" />
            <div className="h-4 bg-gray-100 rounded w-full" />
            <div className="h-4 bg-gray-100 rounded w-full" />
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="h-6 bg-gray-200 rounded w-32 mb-4" />
          <div className="flex gap-2">
            <div className="h-6 bg-gray-100 rounded-full w-20" />
            <div className="h-6 bg-gray-100 rounded-full w-24" />
          </div>
        </div>
      </div>
    </div>
  );
}

function ChallengeHeader({ challenge }: { challenge: ChallengeDetail }) {
  const disciplineColors: Record<string, string> = {
    math: "bg-magic-100 text-magic-700",
    spelling: "bg-pink-100 text-pink-700",
    patterns: "bg-purple-100 text-purple-700",
    typing: "bg-blue-100 text-blue-700",
    programming: "bg-green-100 text-green-700",
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="flex items-start gap-4">
          <div
            className={`
              w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0
              ${disciplineColors[challenge.disciplineSlug] || "bg-gray-100 text-gray-700"}
            `}
          >
            <span className="font-bold text-lg">#{challenge.challengeNumber}</span>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {formatChallengeType(challenge.type)} Challenge
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <span
                className={`
                  inline-flex items-center px-2 py-0.5 rounded text-xs font-medium
                  ${disciplineColors[challenge.disciplineSlug] || "bg-gray-100 text-gray-700"}
                `}
              >
                {challenge.disciplineName}
              </span>
              <span className="text-sm text-gray-500">
                Level {challenge.levelNumber}: {challenge.levelName}
              </span>
            </div>
            <p className="text-xs text-gray-400 font-mono mt-1">
              ID: {challenge.id}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge status={challenge.reviewStatus} />
          {!challenge.isActive && (
            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
              Inactive
            </span>
          )}
          {challenge.aiMetadata && (
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-sunshine-100 text-sunshine-700">
              <SparklesIcon className="w-3 h-3" />
              AI Generated
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function AIMetadataCard({ metadata }: { metadata: AiMetadata }) {
  return (
    <div className="bg-sunshine-50 border border-sunshine-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-3">
        <SparklesIcon className="w-5 h-5 text-sunshine-600" />
        <h3 className="font-semibold text-sunshine-800">AI Generation Details</h3>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
        {metadata.model_used && (
          <div>
            <p className="text-sunshine-600 text-xs">Model</p>
            <p className="font-medium text-sunshine-900">{metadata.model_used}</p>
          </div>
        )}
        {metadata.generated_at && (
          <div>
            <p className="text-sunshine-600 text-xs">Generated</p>
            <p className="font-medium text-sunshine-900">
              {formatRelativeTime(metadata.generated_at)}
            </p>
          </div>
        )}
        {metadata.agent_version && (
          <div>
            <p className="text-sunshine-600 text-xs">Agent Version</p>
            <p className="font-medium text-sunshine-900">{metadata.agent_version}</p>
          </div>
        )}
        {metadata.generation_run_id && (
          <div>
            <p className="text-sunshine-600 text-xs">Run ID</p>
            <p className="font-medium text-sunshine-900 font-mono text-xs truncate">
              {metadata.generation_run_id}
            </p>
          </div>
        )}
      </div>
      {metadata.prompt_summary && (
        <div className="mt-3 pt-3 border-t border-sunshine-200">
          <p className="text-sunshine-600 text-xs mb-1">Prompt Summary</p>
          <p className="text-sm text-sunshine-900">{metadata.prompt_summary}</p>
        </div>
      )}
    </div>
  );
}

function ChallengePreview({
  type,
  discipline,
  content,
}: {
  type: string;
  discipline: DisciplineSlug;
  content: Record<string, unknown>;
}) {
  // Extract common fields based on challenge type
  const prompt = (content.prompt as string) || (content.promptText as string);
  const options = content.options as string[] | undefined;
  const correctAnswer = content.correctAnswer as string | number | undefined;

  return (
    <div className="space-y-4">
      {/* Prompt/Question */}
      {prompt && (
        <div className="text-center">
          <p className="text-xl font-medium text-gray-900">{prompt}</p>
        </div>
      )}

      {/* Type-specific preview */}
      {type === "counting" && (
        <CountingPreview content={content} />
      )}
      {type === "number_recognition" && (
        <NumberRecognitionPreview content={content} />
      )}
      {(type === "addition" || type === "subtraction") && (
        <MathOperationPreview content={content} />
      )}
      {type === "comparison" && (
        <ComparisonPreview content={content} />
      )}
      {type === "letter_recognition" && (
        <LetterRecognitionPreview content={content} />
      )}
      {type === "beginning_sound" && (
        <BeginningSoundPreview content={content} />
      )}
      {type === "word_building" && (
        <WordBuildingPreview content={content} />
      )}
      {(type === "pattern_sequence" || type === "color_pattern") && (
        <PatternPreview content={content} />
      )}
      {type === "key_finding" && (
        <KeyFindingPreview content={content} />
      )}
      {(type === "sequence_typing" || type === "word_typing") && (
        <TypingPreview content={content} />
      )}
      {(type === "maze" || type === "single_command") && (
        <MazePreview content={content} />
      )}

      {/* Generic options display */}
      {options && options.length > 0 && !["maze", "single_command", "word_building"].includes(type) && (
        <div className="flex flex-wrap justify-center gap-3 mt-4">
          {options.map((option, i) => (
            <div
              key={i}
              className={`
                px-4 py-2 rounded-lg border-2 text-lg font-medium
                ${String(option) === String(correctAnswer)
                  ? "border-green-500 bg-green-50 text-green-700"
                  : "border-gray-200 bg-white text-gray-700"}
              `}
            >
              {option}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Challenge type-specific preview components
function CountingPreview({ content }: { content: Record<string, unknown> }) {
  const count = (content.count as number) || (content.correctAnswer as number);
  return (
    <div className="flex justify-center gap-2">
      {Array.from({ length: count || 0 }).map((_, i) => (
        <div key={i} className="w-8 h-8 bg-magic-200 rounded-full" />
      ))}
    </div>
  );
}

function NumberRecognitionPreview({ content }: { content: Record<string, unknown> }) {
  const targetNumber = content.targetNumber as number;
  return (
    <div className="text-center">
      <span className="text-6xl font-bold text-magic-600">{targetNumber}</span>
    </div>
  );
}

function MathOperationPreview({ content }: { content: Record<string, unknown> }) {
  const operand1 = content.operand1 as number;
  const operand2 = content.operand2 as number;
  const operator = content.operator as string;
  return (
    <div className="text-center">
      <span className="text-4xl font-bold text-gray-900">
        {operand1} {operator === "+" ? "+" : "-"} {operand2} = ?
      </span>
    </div>
  );
}

function ComparisonPreview({ content }: { content: Record<string, unknown> }) {
  const number1 = content.number1 as number;
  const number2 = content.number2 as number;
  return (
    <div className="text-center">
      <span className="text-4xl font-bold text-gray-900">
        {number1} ? {number2}
      </span>
    </div>
  );
}

function LetterRecognitionPreview({ content }: { content: Record<string, unknown> }) {
  const targetLetter = content.targetLetter as string;
  return (
    <div className="text-center">
      <span className="text-6xl font-bold text-pink-600">{targetLetter}</span>
    </div>
  );
}

function BeginningSoundPreview({ content }: { content: Record<string, unknown> }) {
  const word = content.word as string;
  const imageUrl = content.imageUrl as string;
  return (
    <div className="text-center space-y-2">
      {imageUrl && (
        <div className="w-24 h-24 mx-auto bg-pink-100 rounded-lg flex items-center justify-center">
          <span className="text-4xl">?</span>
        </div>
      )}
      <p className="text-2xl font-bold text-pink-600">{word}</p>
    </div>
  );
}

function WordBuildingPreview({ content }: { content: Record<string, unknown> }) {
  const targetWord = content.targetWord as string;
  const availableLetters = content.availableLetters as string[];
  return (
    <div className="text-center space-y-4">
      <p className="text-2xl font-bold text-pink-600">{targetWord}</p>
      <div className="flex justify-center gap-2">
        {availableLetters?.map((letter, i) => (
          <div
            key={i}
            className="w-10 h-10 bg-pink-100 rounded-lg flex items-center justify-center
              text-lg font-bold text-pink-700"
          >
            {letter}
          </div>
        ))}
      </div>
    </div>
  );
}

function PatternPreview({ content }: { content: Record<string, unknown> }) {
  const pattern = content.pattern as string[];
  const missingIndex = content.missingIndex as number;
  return (
    <div className="flex justify-center gap-2">
      {pattern?.map((item, i) => (
        <div
          key={i}
          className={`
            w-12 h-12 rounded-lg flex items-center justify-center text-lg font-bold
            ${i === missingIndex
              ? "border-2 border-dashed border-purple-400 text-purple-400"
              : "bg-purple-100 text-purple-700"}
          `}
        >
          {i === missingIndex ? "?" : item}
        </div>
      ))}
    </div>
  );
}

function KeyFindingPreview({ content }: { content: Record<string, unknown> }) {
  const targetKey = content.targetKey as string;
  return (
    <div className="text-center">
      <div className="inline-block px-6 py-4 bg-gray-200 rounded-lg border-b-4 border-gray-400">
        <span className="text-3xl font-bold text-gray-800">{targetKey}</span>
      </div>
    </div>
  );
}

function TypingPreview({ content }: { content: Record<string, unknown> }) {
  const targetSequence = content.targetSequence as string;
  const targetWord = content.targetWord as string;
  const text = targetSequence || targetWord;
  return (
    <div className="text-center">
      <span className="text-3xl font-mono font-bold text-blue-600 tracking-widest">
        {text}
      </span>
    </div>
  );
}

function MazePreview({ content }: { content: Record<string, unknown> }) {
  const gridSize = content.gridSize as { width: number; height: number };
  const start = content.start as { x: number; y: number };
  const end = content.end as { x: number; y: number };
  const obstacles = content.obstacles as Array<{ x: number; y: number }> || [];

  if (!gridSize) {
    return <p className="text-gray-500 text-center">Invalid maze data</p>;
  }

  return (
    <div className="flex justify-center">
      <div
        className="grid gap-1"
        style={{
          gridTemplateColumns: `repeat(${gridSize.width}, minmax(0, 1fr))`,
        }}
      >
        {Array.from({ length: gridSize.height }).map((_, y) =>
          Array.from({ length: gridSize.width }).map((_, x) => {
            const isStart = start?.x === x && start?.y === y;
            const isEnd = end?.x === x && end?.y === y;
            const isObstacle = obstacles.some((o) => o.x === x && o.y === y);

            return (
              <div
                key={`${x}-${y}`}
                className={`
                  w-8 h-8 rounded flex items-center justify-center text-xs font-bold
                  ${isStart ? "bg-green-500 text-white" : ""}
                  ${isEnd ? "bg-blue-500 text-white" : ""}
                  ${isObstacle ? "bg-gray-800" : ""}
                  ${!isStart && !isEnd && !isObstacle ? "bg-gray-100" : ""}
                `}
              >
                {isStart ? "S" : isEnd ? "E" : ""}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function InfoRow({
  label,
  value,
  valueClassName,
}: {
  label: string;
  value: string;
  valueClassName?: string;
}) {
  return (
    <div className="flex justify-between py-2 border-b border-gray-100 last:border-0">
      <span className="text-sm text-gray-500">{label}</span>
      <span className={`text-sm text-gray-900 ${valueClassName || ""}`}>
        {value}
      </span>
    </div>
  );
}

function SkillBadge({
  skill,
  discipline,
}: {
  skill: string;
  discipline: string;
}) {
  const disciplineColors: Record<string, string> = {
    math: "bg-magic-50 text-magic-600 border-magic-200",
    spelling: "bg-pink-50 text-pink-600 border-pink-200",
    patterns: "bg-purple-50 text-purple-600 border-purple-200",
    typing: "bg-blue-50 text-blue-600 border-blue-200",
    programming: "bg-green-50 text-green-600 border-green-200",
  };

  return (
    <span
      className={`
        inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border
        ${disciplineColors[discipline] || "bg-gray-50 text-gray-600 border-gray-200"}
      `}
    >
      {formatSkillName(skill)}
    </span>
  );
}

function StatusBadge({ status }: { status: string }) {
  const statusStyles: Record<string, string> = {
    pending: "bg-yellow-100 text-yellow-700",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
    needs_revision: "bg-orange-100 text-orange-700",
  };

  const statusNames: Record<string, string> = {
    pending: "Pending",
    approved: "Approved",
    rejected: "Rejected",
    needs_revision: "Needs Revision",
  };

  return (
    <span
      className={`
        inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium
        ${statusStyles[status] || "bg-gray-100 text-gray-700"}
      `}
    >
      {statusNames[status] || status}
    </span>
  );
}

// ----- Helper Functions -----

function formatChallengeType(type: string): string {
  return type
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatSkillName(skillKey: string): string {
  return skillKey
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

function formatDateTime(dateString: string): string {
  return new Date(dateString).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffHours < 24) return `${diffHours} hours ago`;
  if (diffDays === 1) return "Yesterday";
  if (diffDays < 7) return `${diffDays} days ago`;

  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

// ----- Icons -----

function ArrowLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
    </svg>
  );
}

function CheckIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function TagIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
    </svg>
  );
}

export default ChallengeDetailPage;
